import React, { useCallback, useState } from "react";
import { ArrowRightIcon, IdentificationIcon } from "@heroicons/react/24/solid";
import { useRecoilValue } from "recoil";

import { toast } from "../../../../components/toastNotification";

import BuslicenseAndPermitCard from "./Dashboard/BLPSummaryCard";
import BuslicenseAndPermitDistributionChart from "./Dashboard/BLPDistributionChart";
import RenewalStatusChart from "./Dashboard/RenewalStatusChart";
import LicensesOfficeChart from "./Dashboard/LicensesOfficeChart";
import RecentActivity from "./Dashboard/RecentActivityCard";
import BLPDashboardSkeleton from "./Dashboard/BLPDashboardSkeleton";
import { useBLPDashboard } from "../api/getBLPDashboard";
import { accountAtom } from "../../../Login/atoms/AccountAtom";
import { useDownloadBLPDashboardExcel } from "../api/downloadBLPDashboardExcel";

interface BLPDashboardProps {
  onGoToTable: (status?: string) => void;
  onViewAllRecent: () => void;
  onRecordClick: (recordIds: string[]) => void;
}

const BLPDashboard: React.FC<BLPDashboardProps> = ({
  onGoToTable,
  onViewAllRecent,
  onRecordClick,
}) => {
  const account = useRecoilValue(accountAtom);

  const [appliedOfficeRiskIds, setAppliedOfficeRiskIds] = useState<number[]>(
    []
  );

  const {
    mutateAsync: downloadBLPDashboardExcel,
    isLoading: downloadingDashboard,
  } = useDownloadBLPDashboardExcel();

  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
  } = useBLPDashboard(
    account.isFranchise,
    account.isNonFranchise,
    Boolean(account.token)
  );

  const summary = dashboardData?.summaryCounts ?? {
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0,
    expired: 0,
    expiring: 0,
  };

  const handleDashboardDownload = useCallback(async (): Promise<void> => {
    try {
      await downloadBLPDashboardExcel({
        officeIds: appliedOfficeRiskIds,

        officeRiskTop:
          appliedOfficeRiskIds.length > 0 ? appliedOfficeRiskIds.length : 10,
      });

      toast.fire(
        "Success",
        "Dashboard file downloaded successfully",
        "success"
      );
    } catch {
      toast.fire("Error", "Failed to download dashboard file", "error");
    }
  }, [appliedOfficeRiskIds, downloadBLPDashboardExcel]);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "35px",
              height: "35px",
              borderRadius: "8px",
              backgroundColor: "#0C77BD",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IdentificationIcon
              style={{
                width: "24px",
                height: "24px",
                color: "#ffffff",
              }}
            />
          </div>

          <div>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: "13px",
                color: "#6B7280",
                fontFamily: "'Segoe UI', system-ui, sans-serif",
              }}
            >
              Business License and Permits /{" "}
              <strong
                style={{
                  color: "#6D6D6D",
                }}
              >
                Dashboard
              </strong>
            </p>

            <h1
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: 700,
                color: "#0D2B6B",
                textTransform: "uppercase",
                fontFamily: "'Segoe UI', system-ui, sans-serif",
              }}
            >
              Business License and Permit Codes
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDashboardDownload}
            disabled={downloadingDashboard}
            className="inline-flex group items-center gap-2 hover:scale-90 transition duration-500 shadow-md px-3 py-1 h-7 text-xs font-semibold text-[#1565c0] border border-[#1565c0] hover:bg-[#1565c0] hover:text-white rounded-full disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="inline-flex items-center justify-center rounded-full h-4 w-4 bg-[#1565c0] group-hover:bg-white transition">
              <ArrowRightIcon
                className="h-3 w-3 text-white group-hover:text-quaternary rotate-90"
                aria-hidden="true"
              />
            </span>

            {downloadingDashboard ? "Downloading..." : "Download File"}
          </button>

          <button
            type="button"
            onClick={() => onGoToTable()}
            className="inline-flex group items-center gap-2 hover:scale-90 transition duration-500 shadow-md px-3 py-1 h-7 text-xs font-semibold text-quaternary border border-quaternary hover:bg-quaternary hover:text-white rounded-full"
          >
            <span className="inline-flex items-center justify-center rounded-full h-4 w-4 bg-quaternary group-hover:bg-white transition">
              <ArrowRightIcon
                className="h-3 w-3 text-white group-hover:text-quaternary"
                aria-hidden="true"
              />
            </span>

            <span>Go to Business Licenses Table</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <BLPDashboardSkeleton />
      ) : (
        <>
          {isError && (
            <div className="my-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              Failed to load Business License and Permit dashboard:{" "}
              {error instanceof Error ? error.message : "Unknown error"}
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              marginTop: "16px",
            }}
          >
            <BuslicenseAndPermitCard
              variant="total"
              count={summary.total}
              onClick={() => onGoToTable()}
            />

            <BuslicenseAndPermitCard
              variant="active"
              count={summary.active}
              onClick={() => onGoToTable("Active")}
            />

            <BuslicenseAndPermitCard
              variant="inactive"
              count={summary.inactive}
              onClick={() => onGoToTable("Inactive")}
            />

            <BuslicenseAndPermitCard
              variant="pending"
              count={summary.pending}
              onClick={() => onGoToTable("Pending")}
            />

            <BuslicenseAndPermitCard
              variant="expired"
              count={summary.expired}
              onClick={() => onGoToTable("Expired")}
            />

            <BuslicenseAndPermitCard
              variant="expiring"
              count={summary.expiring}
              onClick={() => onGoToTable("Expiring")}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              marginTop: "16px",
            }}
          >
            <BuslicenseAndPermitDistributionChart
              data={dashboardData?.distributionByType ?? []}
              isLoading={false}
              isError={isError}
            />

            <RenewalStatusChart
              data={dashboardData?.renewalStatus}
              isLoading={false}
              isError={isError}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 7fr) minmax(0, 3fr)",
              gap: "16px",
              alignItems: "stretch",
              marginTop: "16px",
            }}
          >
            <LicensesOfficeChart
              data={dashboardData?.officeRisk ?? []}
              isLoading={isLoading}
              isError={isError}
              onAppliedOfficeIdsChange={setAppliedOfficeRiskIds}
            />

            <RecentActivity
              onViewAll={onViewAllRecent}
              onRecordClick={onRecordClick}
            />
          </div>
        </>
      )}
    </>
  );
};

export default BLPDashboard;
